import React, { useState, useMemo, useContext } from "react";
import {
  Button,
  Card,
  CardBody,
  Row,
  Col,
  Input,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  CardFooter,
  Badge
} from "reactstrap";
import { DataContext } from "../context/DataContext";
import Select from "react-select";
import { createTerritory, updateTerritory } from "../context/api";

const Territories = () => {
  const { data, setData } = useContext(DataContext);
  const initialTerritories = data.territories || [];
  const regions = data.regions || [];
  const [territories, setTerritories] = useState(initialTerritories);
  const [hover, setHover] = useState(false);
  const isSuperuser = data.userProfile?.is_superuser === true;

  // Filter and modal state
  const [selectedRegion, setSelectedRegion] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Form data includes email flag
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    code: "",
    region: "",
    assignCustomers: { id: [] },
    assignReps: [],
    archived: false,
    email: false
  });

  // Filtered and sorted list
  const filteredTerritories = useMemo(() => {
    let list = territories;
    if (selectedRegion) {
      list = list.filter(
        t => t.region.toLowerCase() === selectedRegion.toLowerCase()
      );
    }
    if (!showArchived) list = list.filter(t => !t.archived);
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [territories, selectedRegion, showArchived]);

  // Open detail modal
  const openDetailModal = territory => {
    setFormData({ ...territory });
    setIsEditing(false);
    setFormErrors({});
    setDetailModalOpen(true);
  };
  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setFormErrors({});
  };

  // Open add modal
  const openAddModal = () => {
    setFormData({
      id: null,
      name: "",
      code: "",
      region: "",
      assignCustomers: { id: [] },
      assignReps: [],
      archived: false,
      email: false
    });
    setIsEditing(true);
    setFormErrors({});
    setAddModalOpen(true);
  };
  const closeAddModal = () => {
    setAddModalOpen(false);
    setFormErrors({});
  };

  // Handle text and checkbox change
  const handleFormChange = e => {
    const { name, type, value, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: val }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: null }));
  };

  // Validation
  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = "Territory Name is required.";
    if (!formData.region.trim()) errors.region = "Region is required.";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Save or update
  const handleSaveTerritory = async () => {
    if (!validateForm()) return;
    const payload = { ...formData, code: '001' };
    if (formData.id) {
      // update
      const old = territories;
      const updated = territories.map(t =>
        t.id === formData.id ? payload : t
      );
      setTerritories(updated);
      setData(d => ({ ...d, territories: updated }));
      try {
        await updateTerritory(formData.id, payload);
      } catch {
        setTerritories(old);
        setData(d => ({ ...d, territories: old }));
      }
    } else {
      // create
      const old = territories;
      const tempId = Date.now();
      const newT = { ...payload, id: tempId };
      const added = [...territories, newT];
      setTerritories(added);
      setData(d => ({ ...d, territories: added }));
      try {
        const saved = await createTerritory(payload);
        const replaced = added.map(t =>
          t.id === tempId ? saved : t
        );
        setTerritories(replaced);
        setData(d => ({ ...d, territories: replaced }));
      } catch {
        setTerritories(old);
        setData(d => ({ ...d, territories: old }));
      }
    }
    closeDetailModal();
    closeAddModal();
  };

  // Archive toggle
  const handleToggleArchive = async () => {
    const old = territories;
    const updated = territories.map(t =>
      t.id === formData.id ? { ...t, archived: !t.archived } : t
    );
    setTerritories(updated);
    setData(d => ({ ...d, territories: updated }));
    closeDetailModal();
    try {
      const newFlag = updated.find(t => t.id === formData.id).archived;
      await updateTerritory(formData.id, { archived: newFlag });
    } catch {
      setTerritories(old);
      setData(d => ({ ...d, territories: old }));
    }
  };

  return (
    <div className="content">
      <Row className="align-items-center mb-3">
        <Col md="4">
          <FormGroup>
            <Input
              type="select"
              value={selectedRegion}
              onChange={e => setSelectedRegion(e.target.value)}
            >
              <option value="">All Regions</option>
              {regions
                .filter(r => !r.is_archived)
                .map(r => (
                  <option key={r.id} value={r.name}>
                    {r.name}
                  </option>
                ))}
            </Input>
          </FormGroup>
        </Col>
        <Col md="4" className="d-flex justify-content-center">
          <FormGroup check>
            <Label
              check
              className={`p-2 rounded ${
                showArchived ? 'bg-secondary text-white' : 'bg-light text-dark'
              }`}
            >
              <Input
                type="checkbox"
                checked={showArchived}
                onChange={e => setShowArchived(e.target.checked)}
              />{' '}
              {showArchived ? 'Remove Archived' : 'Show Archived'}
            </Label>
          </FormGroup>
        </Col>
        {isSuperuser && (
          <Col
            md="4"
            className="text-center"
            style={{ cursor: 'pointer' }}
            onClick={openAddModal}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
          >
            <i className="nc-icon nc-vector" style={{ fontSize: '1.5rem' }} />
            <div>Add New Territory</div>
          </Col>
        )}
      </Row>

      <Row>
        {filteredTerritories.map(t => (
          <Col key={t.id} xs="12" sm="6" md="4" lg="3" className="mb-3">
            <Card
              onClick={() => openDetailModal(t)}
              className="h-100"
              style={{ cursor: 'pointer' }}
            >
              <CardBody>
                <h5>{t.name}</h5>
                <p>Region: {t.region}</p>
                <p>
                  Account Email:{' '}
                  <Badge color={t.email ? 'success' : 'danger'}>
                    {t.email ? 'Enabled' : 'Disabled'}
                  </Badge>
                </p>
              </CardBody>
              <CardFooter>
                {t.archived && <Badge color="warning">Archived</Badge>}
              </CardFooter>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Detail Modal */}
      <Modal isOpen={detailModalOpen} toggle={closeDetailModal}>
        <ModalHeader toggle={closeDetailModal}>Territory Details</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="territoryName">Territory Name</Label>
            <Input
              type="text"
              id="territoryName"
              name="name"
              value={formData.name}
              onChange={handleFormChange}
              disabled={!isEditing}
              invalid={!!formErrors.name}
            />
            {formErrors.name && (
              <div className="text-danger small">{formErrors.name}</div>
            )}
          </FormGroup>
          <FormGroup>
            <Label for="territoryRegion">Region</Label>
            <Input
              type="select"
              id="territoryRegion"
              name="region"
              value={formData.region}
              onChange={handleFormChange}
              disabled={!isEditing}
              invalid={!!formErrors.region}
            >
              <option value="">Select Region</option>
              {regions
                .filter(r => !r.is_archived)
                .map(r => (
                  <option key={r.id} value={r.name}>
                    {r.name}
                  </option>
                ))}
            </Input>
            {formErrors.region && (
              <div className="text-danger small">{formErrors.region}</div>
            )}
          </FormGroup>
          <FormGroup>
          <Col className="mb-3">
            <Button
              color={formData.email ? "danger" : "primary"}
              onClick={() => setFormData(prev => ({ ...prev, email: !prev.email }))}
              disabled={!isEditing}
              block
            >
              {formData.email
                ? "Disable Accounts Email"
                : "Enable Accounts Email"}
            </Button>
          </Col>

          </FormGroup>

        </ModalBody>
        <ModalFooter>
          {isEditing ? (
            <>
              <Button
                color="primary"
                onClick={handleSaveTerritory}
                disabled={Object.values(formErrors).some(e => e)}
              >
                Save Changes
              </Button>
              <Button
                color="secondary"
                onClick={() => {
                  setIsEditing(false);
                  setFormErrors({});
                }}
              >
                Cancel Editing
              </Button>
            </>
          ) : (
            isSuperuser && (
              <Button color="primary" onClick={() => setIsEditing(true)}>
                Edit Territory
              </Button>
            )
          )}
          {formData.id && !isEditing && isSuperuser && (
            <Button
              color={formData.archived ? 'warning' : 'danger'}
              onClick={handleToggleArchive}
            >
              {formData.archived ? 'Unarchive Territory' : 'Archive Territory'}
            </Button>
          )}
          <Button color="secondary" onClick={closeDetailModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Add Modal */}
      <Modal isOpen={addModalOpen} toggle={closeAddModal}>
        <ModalHeader toggle={closeAddModal}>Add New Territory</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="newTerritoryName">Territory Name</Label>
            <Input
              type="text"
              id="newTerritoryName"
              name="name"
              value={formData.name}
              onChange={handleFormChange}
              invalid={!!formErrors.name}
            />
            {formErrors.name && (
              <div className="text-danger small">{formErrors.name}</div>
            )}
          </FormGroup>
          <FormGroup>
            <Label for="newTerritoryRegion">Region</Label>
            <Input
              type="select"
              id="newTerritoryRegion"
              name="region"
              value={formData.region}
              onChange={handleFormChange}
              invalid={!!formErrors.region}
            >
              <option value="">Select Region</option>
              {regions
                .filter(r => !r.is_archived)
                .map(r => (
                  <option key={r.id} value={r.name}>
                    {r.name}
                  </option>
                ))}
            </Input>
            {formErrors.region && (
              <div className="text-danger small">{formErrors.region}</div>
            )}
          </FormGroup>
         <FormGroup>
          <Col className="mb-3">
            <Button
              color={formData.email ? "danger" : "primary"}
              onClick={() => setFormData(prev => ({ ...prev, email: !prev.email }))}
              disabled={!isEditing}
              block
            >
              {formData.email
                ? "Disable Accounts Email"
                : "Enable Accounts Email"}
            </Button>
          </Col>

          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={handleSaveTerritory}
            disabled={Object.values(formErrors).some(e => e)}
          >
            Add Territory
          </Button>
          <Button color="secondary" onClick={closeAddModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Territories;
